import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import GlobalContext from "../../context/GlobalContext";
import Select from "../Core/Select";
import { useAuth } from "../../context/AuthContext";
import { fetchUserById } from "../../services/auth";
import { useUserPlan } from "../../context/UserPlanContext";
import { planTypeFromPlanId } from "../../utils/planUtils";
import { submitActivationForm, fetchStatesByZone, fetchCitiesByStates, fetchUserPlanIdForActivationForm, fetchUserPlanByPlanId, fetchGstInfo, fetchProductsBySubcategory, fetchKeywordsBySubcategory, fetchKeywordsByProduct } from "../../services/tenders";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GSTIN_LENGTH = 15;
const FORM_PLAN_TYPES = ["freemium", "enterprise", "insights", "bidding", "gem"];
const TENDER_STATE_SENTINELS = new Set(["upto5", "up_to_5", "up to 5", "5_states", "5 states", "five_states", "upto1", "up_to_1", "up to 1", "1_state", "1 state", "one_state", "allindia", "all_india", "all india"]);
const UPTO_1_STATE_SENTINELS = new Set(["upto1", "up_to_1", "up to 1", "1_state", "1 state", "one_state"]);
const UPTO_5_STATE_SENTINELS = new Set(["upto5", "up_to_5", "up to 5", "5_states", "5 states", "five_states"]);
const ALL_INDIA_SENTINELS = new Set(["allindia", "all_india", "all india"]);
const ALL_INDIA_STATE_OPTION = { value: "allindia", label: "All India" };
const COMPANY_TYPE_OPTIONS = [
  { value: "proprietorship", label: "Proprietorship" },
  { value: "partnership", label: "Partnership" },
  { value: "pvt_ltd", label: "Private Limited" },
  { value: "llp", label: "LLP" },
  { value: "public_ltd", label: "Public Limited" },
  { value: "govt", label: "Government" },
];

const text = (v) => String(v || "").trim();
const token = (v) => text(v).toLowerCase().replace(/[-\s]+/g, "_");
const getPlanId = (r) => r?.pid ?? r?.pId ?? r?.plan_id ?? r?.planId;
const getUpid = (r) => r?.upid ?? r?.upId ?? r?.up_id;
const normalizePlanType = (v) => {
  if (v == null || v === "") return null;
  const raw = token(v);
  if (FORM_PLAN_TYPES.includes(raw)) return raw;
  if (raw === "tender_bidding") return "bidding";
  if (raw === "gem_consultancy") return "gem";
  if (raw === "pay_as_you_go") return "enterprise";
  if (raw === "dsc") return null;
  const n = Number(v);
  const fromId = Number.isNaN(n) ? null : planTypeFromPlanId(n);
  if (fromId === "tender_bidding") return "bidding";
  if (fromId === "gem_consultancy") return "gem";
  if (fromId === "pay_as_you_go") return "enterprise";
  return FORM_PLAN_TYPES.includes(fromId) ? fromId : null;
};
const listFromValue = (v) => {
  if (Array.isArray(v)) return v.flatMap(listFromValue);
  if (v == null) return [];
  if (typeof v !== "string") return [text(v)].filter(Boolean);
  const t = v.trim();
  if (!t) return [];
  try { const parsed = JSON.parse(t); if (Array.isArray(parsed)) return parsed.flatMap(listFromValue); } catch (_) {}
  return t.replace(/^\{|\}$/g, "").split(",").map((x) => x.trim().replace(/^"|"$/g, "")).filter(Boolean);
};
const unique = (items) => {
  const seen = new Set();
  return (items || []).reduce((out, item) => {
    const value = text(item); const key = value.toLowerCase();
    if (value && !seen.has(key)) { seen.add(key); out.push(value); }
    return out;
  }, []);
};
const getPlanName = (r) => text(r?.planName || r?.plan_name || r?.planname || r?.name || r?.label || (getPlanId(r) ? `Plan ${getPlanId(r)}` : "Plan"));
const getPlanAmount = (r) => {
  const amount = r?.amount ?? r?.Amount ?? r?.plan_amount ?? r?.planAmount ?? r?.price ?? r?.Price;
  if (amount === undefined || amount === null || amount === "") return "0/-";
  const n = Number(String(amount).replace(/,/g, ""));
  return !Number.isNaN(n) && n > 0 ? `${Math.round(n).toLocaleString("en-IN")}/-` : `${amount}/-`;
};
const planStateTokens = (r) => [r?.states, r?.selectedstates, r?.selectedStates, r?.statetype, r?.state_type, r?.zone, r?.zoneSummary]
  .flatMap(listFromValue)
  .map(token);
const needsTenderStates = (r) => planStateTokens(r).some((v) => TENDER_STATE_SENTINELS.has(v));
const isAllIndia = (r) => planStateTokens(r).some((v) => ALL_INDIA_SENTINELS.has(v));
const isUpto1 = (r) => planStateTokens(r).some((v) => UPTO_1_STATE_SENTINELS.has(v));
const isUpto5 = (r) => planStateTokens(r).some((v) => UPTO_5_STATE_SENTINELS.has(v));
const companyTypeFromGst = (v) => {
  const raw = text(v).toLowerCase();
  if (raw.includes("private limited")) return "pvt_ltd";
  if (raw.includes("public limited")) return "public_ltd";
  if (raw.includes("limited liability partnership") || raw === "llp") return "llp";
  if (raw.includes("partnership")) return "partnership";
  if (raw.includes("proprietor") || raw.includes("proprietorship")) return "proprietorship";
  if (raw.includes("government") || raw.includes("govt")) return "govt";
  return "";
};

const ActivationFormMain = () => {
  const gContext = useContext(GlobalContext);
  const { user, refreshUserPlans } = useAuth();
  const { loadPlanDetails } = useUserPlan();
  const firstInputRef = useRef(null);
  const lastFetchedGstinRef = useRef("");
  const previousOpenRef = useRef(false);
  const [formData, setFormData] = useState({});
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState("idle");
  const [submitApiError, setSubmitApiError] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const [userPlansForActivation, setUserPlansForActivation] = useState([]);
  const [hasLoadedUserPlans, setHasLoadedUserPlans] = useState(false);
  const [userPlanDetails, setUserPlanDetails] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstLockedFields, setGstLockedFields] = useState({});
  const [productOptions, setProductOptions] = useState([]);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState("");
  const isOpen = Boolean(gContext.activationFormVisible);
  const planTypeOnly = gContext.activationFormPlanType || null;
  const planTypeFilter = useMemo(() => unique((Array.isArray(planTypeOnly) ? planTypeOnly : [planTypeOnly]).map(normalizePlanType).filter(Boolean)), [planTypeOnly]);
  const resetState = useCallback(() => {
    setFormData({}); setTouched({}); setErrors({}); setSubmitting(false); setSubmitState("idle"); setSubmitApiError("");
    setUserPlansForActivation([]); setHasLoadedUserPlans(false); setUserPlanDetails([]); setStates([]); setCities([]); setProductOptions([]); setKeywordOptions([]);
    setLoadingStates(false); setLoadingCities(false); setLoadingProducts(false); setLoadingKeywords(false); setGstLoading(false); setGstLockedFields({}); setTermsAccepted(false); setTermsError("");
    lastFetchedGstinRef.current = "";
  }, []);
  const close = useCallback(() => {
    if (!isOpen || isClosing) return;
    setIsClosing(true);
    window.setTimeout(() => { setIsClosing(false); gContext.toggleActivationForm(); resetState(); }, 160);
  }, [gContext, isClosing, isOpen, resetState]);  useEffect(() => { if (isOpen && !previousOpenRef.current) resetState(); previousOpenRef.current = isOpen; }, [isOpen, resetState]);
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKeyDown);
    window.setTimeout(() => firstInputRef.current?.focus?.(), 0);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close, isOpen]);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const uid = user?.id ?? user?.uid ?? user?.user_id ?? null;
      if (mounted) setHasLoadedUserPlans(false);
      if (uid == null) { if (mounted) { setUserPlansForActivation([]); setHasLoadedUserPlans(true); } return; }
      try {
        const response = await fetchUserPlanIdForActivationForm(uid);
        if (mounted) setUserPlansForActivation(Array.isArray(response?.user_plans) ? response.user_plans : []);
      } catch (error) { console.error("Error loading user plan IDs for activation form:", error); if (mounted) setUserPlansForActivation([]); }
      finally { if (mounted) setHasLoadedUserPlans(true); }
    };
    if (isOpen) load();
    return () => { mounted = false; };
  }, [isOpen, user?.id, user?.uid, user?.user_id]);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!userPlansForActivation.length) { setUserPlanDetails([]); return; }
      try {
        const details = await Promise.all(userPlansForActivation.map((p) => fetchUserPlanByPlanId(getUpid(p))));
        if (!mounted) return;
        setUserPlanDetails(details.map((detail, i) => {
          const source = userPlansForActivation[i];
          if (!detail) return source;
          const upid = getUpid(detail) ?? getUpid(source);
          return { ...source, ...detail, upid, upId: upid };
        }));
      } catch (error) { console.error("Error loading user plan details:", error); if (mounted) setUserPlanDetails(userPlansForActivation); }
    };
    if (isOpen) load();
    return () => { mounted = false; };
  }, [isOpen, userPlansForActivation]);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const uid = user?.id ?? user?.uid ?? user?.user_id ?? null;
      if (uid == null) return;
      try {
        const profile = await fetchUserById(uid);
        if (!mounted || !profile || typeof profile !== "object") return;
        setFormData((prev) => ({
          ...prev,
          ...(text(profile.uname ?? profile.UName) ? { contactPerson: text(profile.uname ?? profile.UName) } : {}),
          ...(text(profile.email) ? { email: text(profile.email) } : {}),
          ...(text(profile.phone ?? profile.mobile) ? { mobile: text(profile.phone ?? profile.mobile).replace(/\s/g, "") } : {}),
          ...(text(profile.state) ? { state: text(profile.state) } : {}),
          ...(text(profile.city ?? profile.City) ? { city: text(profile.city ?? profile.City) } : {}),
        }));
      } catch (error) { console.error("Error loading user profile for activation form:", error); }
    };
    if (isOpen) load();
    return () => { mounted = false; };
  }, [isOpen, user?.id, user?.uid, user?.user_id]);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoadingStates(true);
      try { const result = await fetchStatesByZone(null); if (mounted) setStates(Array.isArray(result) ? result : []); }
      catch (error) { console.error("Error loading states:", error); if (mounted) setStates([]); }
      finally { if (mounted) setLoadingStates(false); }
    };
    if (isOpen) load();
    return () => { mounted = false; };
  }, [isOpen]);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoadingCities(true);
      try { const result = await fetchCitiesByStates(text(formData.state) ? [text(formData.state)] : []); if (mounted) setCities(Array.isArray(result) ? result : []); }
      catch (error) { console.error("Error loading cities:", error); if (mounted) setCities([]); }
      finally { if (mounted) setLoadingCities(false); }
    };
    if (isOpen) load();
    return () => { mounted = false; };
  }, [isOpen, formData.state]);

  const activationPlanRows = useMemo(() => {
    const detailsByUpid = new Map((userPlanDetails || []).map((d) => [String(getUpid(d)), d]));
    return (userPlansForActivation || []).map((p) => {
      const detail = detailsByUpid.get(String(getUpid(p))) || {};
      const upid = getUpid(detail) ?? getUpid(p);
      return { ...p, ...detail, upid, upId: upid };
    });
  }, [userPlanDetails, userPlansForActivation]);
  const targetRows = useMemo(() => activationPlanRows.filter((row) => {
    const typeValue = normalizePlanType(row?.planType ?? row?.plantype ?? row?.plan_type ?? getPlanId(row));
    return typeValue && (planTypeFilter.length === 0 || planTypeFilter.includes(typeValue));
  }), [activationPlanRows, planTypeFilter]);
  const loading = isOpen && !hasLoadedUserPlans;
  const hasGemPlan = useMemo(() => targetRows.some((row) => normalizePlanType(row?.planType ?? row?.plantype ?? row?.plan_type ?? getPlanId(row)) === "gem"), [targetRows]);
  const needsStateSelection = useMemo(() => targetRows.some(needsTenderStates), [targetRows]);
  const hasAllIndiaStatePlan = useMemo(() => targetRows.some(isAllIndia), [targetRows]);
  const tenderStateLimit = useMemo(() => {
    if (hasAllIndiaStatePlan) return null;
    if (targetRows.some(isUpto1)) return 1;
    if (targetRows.some(isUpto5)) return 5;
    return null;
  }, [hasAllIndiaStatePlan, targetRows]);
  const selectedTenderStates = useMemo(() => unique(Array.isArray(formData.tenderStates) ? formData.tenderStates : []), [formData.tenderStates]);
  const selectedSubcategories = useMemo(() => unique(targetRows.flatMap((row) => listFromValue(row?.selectedcategories ?? row?.selectedCategories ?? row?.subcategory ?? row?.subCategory))), [targetRows]);
  const selectedProducts = useMemo(() => unique(Array.isArray(formData.products) ? formData.products : []), [formData.products]);
  const selectedKeywords = useMemo(() => unique(Array.isArray(formData.keywords) ? formData.keywords : []), [formData.keywords]);
  const productKeywords = useMemo(() => unique(listFromValue(formData.productKeywords)), [formData.productKeywords]);
  const displaySummary = gContext.activationFormDisplaySummary || {};
  const combinedPlanText = useMemo(() => text(displaySummary.planText) || targetRows.map(getPlanName).filter(Boolean).join(" + "), [displaySummary.planText, targetRows]);
  const combinedCostText = useMemo(() => text(displaySummary.costText) || targetRows.map(getPlanAmount).filter(Boolean).join(" + "), [displaySummary.costText, targetRows]);
  const subcategoryOptions = useMemo(() => selectedSubcategories.map((s) => ({ value: s, label: s })), [selectedSubcategories]);
  const selectedSubcategoryOptions = useMemo(() => selectedSubcategories.map((s) => ({ value: s, label: s })), [selectedSubcategories]);
  const productSelectOptions = useMemo(() => productOptions.map((p) => ({ value: p, label: p })), [productOptions]);
  const keywordSelectOptions = useMemo(() => keywordOptions.map((k) => ({ value: k, label: k })), [keywordOptions]);
  useEffect(() => {
    if (!isOpen || !hasAllIndiaStatePlan || selectedTenderStates.length > 0) return;
    setFormData((prev) => ({ ...prev, tenderStates: [ALL_INDIA_STATE_OPTION.value] }));
  }, [hasAllIndiaStatePlan, isOpen, selectedTenderStates.length]);
  useEffect(() => {
    let mounted = true;
    const loadProducts = async () => {
      if (!selectedSubcategories.length) {
        setProductOptions([]);
        setFormData((prev) => ({ ...prev, products: [], keywords: [] }));
        return;
      }
      setLoadingProducts(true);
      try {
        const result = await fetchProductsBySubcategory(selectedSubcategories, 1, 1000);
        if (!mounted) return;
        const options = unique((Array.isArray(result?.data) ? result.data : []).map((item) => item?.product ?? item?.name ?? item?.value)).sort();
        setProductOptions(options);
        setFormData((prev) => ({
          ...prev,
          products: unique(Array.isArray(prev.products) ? prev.products.filter((p) => options.includes(p)) : []),
        }));
      } catch (error) { console.error("Error loading activation products:", error); if (mounted) setProductOptions([]); }
      finally { if (mounted) setLoadingProducts(false); }
    };
    if (isOpen) loadProducts();
    return () => { mounted = false; };
  }, [isOpen, selectedSubcategories]);
  useEffect(() => {
    let mounted = true;
    const loadKeywords = async () => {
      if (!selectedSubcategories.length) {
        setKeywordOptions([]);
        return;
      }
      setLoadingKeywords(true);
      try {
        let rows = [];
        if (selectedProducts.length) {
          const results = await Promise.all(selectedProducts.map((product) => fetchKeywordsByProduct(product, 1, 500, selectedSubcategories.length === 1 ? selectedSubcategories[0] : null)));
          rows = results.flatMap((result) => (Array.isArray(result?.data) ? result.data : []));
        } else {
          const results = await Promise.all(selectedSubcategories.map((subcategory) => fetchKeywordsBySubcategory(subcategory, 1, 500)));
          rows = results.flatMap((result) => (Array.isArray(result?.data) ? result.data : []));
        }
        if (!mounted) return;
        const options = unique(rows.map((item) => item?.keyword ?? item?.name ?? item?.value)).sort();
        setKeywordOptions(options);
        setFormData((prev) => ({
          ...prev,
          keywords: unique(Array.isArray(prev.keywords) ? prev.keywords.filter((k) => options.includes(k)) : []),
        }));
      } catch (error) { console.error("Error loading activation keywords:", error); if (mounted) setKeywordOptions([]); }
      finally { if (mounted) setLoadingKeywords(false); }
    };
    if (isOpen) loadKeywords();
    return () => { mounted = false; };
  }, [isOpen, selectedProducts, selectedSubcategories]);
  useEffect(() => { if (isOpen && hasLoadedUserPlans && targetRows.length === 0) close(); }, [close, hasLoadedUserPlans, isOpen, targetRows.length]);

  const setField = (name, value) => { setFormData((prev) => ({ ...prev, [name]: value })); setErrors((prev) => ({ ...prev, [name]: "" })); setSubmitState("idle"); };
  const markTouched = (name, message = "") => { setTouched((prev) => ({ ...prev, [name]: true })); setErrors((prev) => ({ ...prev, [name]: message })); };
  const mobileError = (v) => { const d = text(v).replace(/\D/g, ""); if (!d) return "Mobile is required"; return d.length === 10 ? "" : "Enter a valid 10-digit mobile number"; };
  const phoneError = (v) => { const d = text(v).replace(/\D/g, ""); if (!d) return ""; return d.length < 6 ? "Enter a valid phone number" : ""; };
  const websiteError = (v) => { const raw = text(v); if (!raw) return ""; try { new URL(raw.startsWith("http") ? raw : `https://${raw}`); return ""; } catch (_) { return "Enter a valid website URL"; } };
  const triggerGstLookup = async (v) => {
    const gst = text(v).replace(/\s/g, "").toUpperCase().slice(0, GSTIN_LENGTH);
    if (gst.length !== GSTIN_LENGTH || lastFetchedGstinRef.current === gst) return;
    setGstLoading(true);
    try {
      const data = await fetchGstInfo(gst);
      const legalName = text(data?.company_name);
      const companyType = companyTypeFromGst(data?.type);
      setFormData((prev) => ({ ...prev, ...(legalName ? { companyName: legalName } : {}), ...(companyType ? { companyType } : {}) }));
      setGstLockedFields({ ...(legalName ? { companyName: true } : {}), ...(companyType ? { companyType: true } : {}) });
      lastFetchedGstinRef.current = gst;
    } catch (_) {} finally { setGstLoading(false); }
  };  const validateForm = () => {
    const next = {};
    if (!text(formData.gst)) next.gst = "GST No. is required";
    if (!text(formData.companyName)) next.companyName = "Company name is required";
    if (!text(formData.companyType)) next.companyType = "Company type is required";
    if (!text(formData.contactPerson)) next.contactPerson = "Contact person is required";
    if (!text(formData.address)) next.address = "Address is required";
    if (!text(formData.state)) next.state = "State is required";
    if (!text(formData.city)) next.city = "City is required";
    const phoneMsg = phoneError(formData.phone); if (phoneMsg) next.phone = phoneMsg;
    const mobileMsg = mobileError(formData.mobile); if (mobileMsg) next.mobile = mobileMsg;
    const email = text(formData.email);
    if (!email) next.email = "Email is required"; else if (!EMAIL_RE.test(email)) next.email = "Enter a valid email address";
    const altEmail = text(formData.altEmail); if (altEmail && !EMAIL_RE.test(altEmail)) next.altEmail = "Enter a valid email address";
    const webMsg = websiteError(formData.website); if (webMsg) next.website = webMsg;
    if (productKeywords.length === 0) next.productKeywords = "Product keywords are required";
    if (needsStateSelection && selectedTenderStates.length === 0) next.tenderStates = "At least 1 state is required";
    if (needsStateSelection && tenderStateLimit && selectedTenderStates.length > tenderStateLimit) next.tenderStates = `Maximum ${tenderStateLimit} state${tenderStateLimit === 1 ? "" : "s"} allowed`;
    if (hasGemPlan && !text(formData.gemUserName)) next.gemUserName = "GeM username/email is required";
    if (hasGemPlan && !text(formData.gemPassword)) next.gemPassword = "GeM password is required";
    if (!combinedPlanText || targetRows.filter((r) => Number(getUpid(r)) > 0).length === 0) next.planSummary = "No pending activation plan found";
    const allTouched = {};
    ["gst", "companyName", "companyType", "contactPerson", "address", "state", "city", "phone", "mobile", "email", "altEmail", "website", "productKeywords", "products", "keywords", "tenderStates", "gemUserName", "gemPassword", "planSummary"].forEach((n) => { allTouched[n] = true; });
    setTouched(allTouched); setErrors(next);
    return Object.keys(next).length === 0;
  };
  const buildPayload = (row) => {
    const uid = user?.id ?? user?.uid ?? user?.user_id ?? 0;
    const rowIsGem = normalizePlanType(row?.planType ?? row?.plantype ?? row?.plan_type ?? getPlanId(row)) === "gem";
    const rowNeedsStates = needsTenderStates(row);
    const rowPlanText = getPlanName(row);
    return {
      uId: typeof uid === "number" ? uid : parseInt(uid, 10) || 0,
      upid: Number(getUpid(row)) || 0,
      gstno: text(formData.gst).replace(/\s/g, "").toUpperCase(),
      companyname: text(formData.companyName),
      companytype: text(formData.companyType),
      contactperson: text(formData.contactPerson),
      designation: text(formData.designation),
      address: text(formData.address),
      city: text(formData.city),
      state: text(formData.state),
      phone: text(formData.phone),
      mobile: text(formData.mobile),
      emailid: text(formData.email),
      altemailid: text(formData.altEmail),
      website_url: text(formData.website),
      ...(rowNeedsStates && selectedTenderStates.length ? { selectedstates: selectedTenderStates } : {}),
      subcategory: selectedSubcategories,
      product: selectedProducts,
      keywords: selectedKeywords,
      productkeywords: productKeywords,
      subscriptionplan: rowPlanText,
      serviceduration: rowPlanText,
      amountdetails: getPlanAmount(row),
      remarks: text(formData.remarks),
      ...(rowIsGem ? { gemusername: text(formData.gemUserName), gempassword: text(formData.gemPassword) } : {}),
    };
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) { setTermsError("Please accept the Terms and Conditions to continue"); return; }
    setTermsError("");
    if (!validateForm()) return;
    const rowsToSubmit = targetRows.filter((row) => Number(getUpid(row)) > 0);
    setSubmitting(true); setSubmitState("idle"); setSubmitApiError("");
    try {
      for (const row of rowsToSubmit) await submitActivationForm(buildPayload(row));
      const uid = user?.id ?? user?.uid ?? user?.user_id ?? null;
      if (uid != null) {
        try { await Promise.all([refreshUserPlans(uid), loadPlanDetails(uid)]); } catch (refreshError) { console.error("Error refreshing user plans after activation:", refreshError); }
      }
      setSubmitState("success");
      window.setTimeout(() => close(), 900);
    } catch (error) {
      console.error("Activation form submit error:", error);
      setSubmitState("error");
      setSubmitApiError(typeof error?.message === "string" && error.message.trim() ? error.message.trim() : "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };
  const fieldError = (name) => touched[name] && errors[name] ? <div className="invalid-feedback d-block">{errors[name]}</div> : null;
  const controlClass = (name) => `form-control ${touched[name] && errors[name] ? "is-invalid" : ""}`;
  const value = (name) => formData[name] ?? "";
  const requiredPlaceholder = (label, show, modifier = "") => show ? (
    <span className={`activation-required-placeholder ${modifier}`} aria-hidden="true">
      {label} <span className="activation-required-asterisk">*</span>
    </span>
  ) : null;
  const requiredSelectPlaceholder = (label) => (
    <span>
      {label} <span className="activation-required-asterisk">*</span>
    </span>
  );
  const input = ({ name, type = "text", placeholder, required = false, rows = 0, validate, readOnly = false, overrideValue }) => {
    const currentValue = overrideValue !== undefined ? overrideValue : value(name);
    const showRequiredPlaceholder = required && String(currentValue ?? "").length === 0;
    const common = {
      id: name, name, className: controlClass(name), placeholder: required ? "" : placeholder, value: currentValue, required, readOnly, "aria-label": placeholder,
      onChange: (event) => {
        if (readOnly) return;
        const raw = event.target.value;
        const next = name === "gst" ? raw.replace(/\s/g, "").toUpperCase().slice(0, GSTIN_LENGTH) : raw;
        setField(name, next);
        if (name === "gst" && next.length === GSTIN_LENGTH) triggerGstLookup(next);
      },
      onBlur: () => { const msg = validate ? validate(value(name)) : ""; markTouched(name, msg); if (name === "gst") triggerGstLookup(value(name)); },
      ref: name === "gst" ? firstInputRef : undefined,
    };
    return <div className={`form-group mb-6 col-12 ${required ? "activation-required-field" : ""}`} key={name}>{rows ? <textarea {...common} rows={rows} /> : <input {...common} type={type} maxLength={name === "gst" ? GSTIN_LENGTH : undefined} />}{requiredPlaceholder(placeholder, showRequiredPlaceholder, rows ? "is-textarea" : "")}{name === "gst" && gstLoading ? <span className="spinner-border spinner-border-sm text-primary mt-2" role="status" aria-hidden="true" /> : null}{fieldError(name)}</div>;
  };
  const renderStateSelect = () => {
    const showPlaceholder = !loadingStates && !text(value("state"));
    return <div className="form-group mb-6 col-12 activation-required-field"><select id="state" name="state" aria-label="State" className={`${controlClass("state")} ${showPlaceholder ? "activation-required-control-empty" : ""}`} value={value("state")} onChange={(e) => { setField("state", e.target.value); setField("city", ""); setTouched((p) => ({ ...p, state: true })); }} onBlur={() => markTouched("state", !text(value("state")) ? "State is required" : "")} disabled={loadingStates} required><option value="">{loadingStates ? "Loading..." : ""}</option>{states.map((s) => <option key={s.value || s.label} value={s.value || s.label}>{s.label || s.value}</option>)}</select>{requiredPlaceholder("State", showPlaceholder)}{fieldError("state")}</div>;
  };
  const renderCitySelect = () => {
    const showPlaceholder = !loadingCities && !text(value("city"));
    return <div className="form-group mb-6 col-12 activation-required-field"><select id="city" name="city" aria-label="City" className={`${controlClass("city")} ${showPlaceholder ? "activation-required-control-empty" : ""}`} value={value("city")} onChange={(e) => { setField("city", e.target.value); setTouched((p) => ({ ...p, city: true })); }} onBlur={() => markTouched("city", !text(value("city")) ? "City is required" : "")} disabled={loadingCities} required><option value="">{loadingCities ? "Loading..." : ""}</option>{cities.map((c) => <option key={c.value || c.label} value={c.value || c.label}>{c.label || c.value}</option>)}</select>{requiredPlaceholder("City", showPlaceholder)}{fieldError("city")}</div>;
  };
  const renderTenderStates = () => {
    if (!needsStateSelection) return null;
    const stateOptions = states.map((s) => ({ value: s.value || s.label || s, label: s.label || s.value || s }));
    const options = hasAllIndiaStatePlan ? [ALL_INDIA_STATE_OPTION, ...stateOptions.filter((o) => token(o.value) !== ALL_INDIA_STATE_OPTION.value)] : stateOptions;
    const selected = selectedTenderStates.map((s) => options.find((o) => String(o.value).toLowerCase() === String(s).toLowerCase()) || { value: s, label: token(s) === ALL_INDIA_STATE_OPTION.value ? ALL_INDIA_STATE_OPTION.label : s });
    const placeholder = tenderStateLimit ? `Select states (max ${tenderStateLimit})` : "Select states";
    return <div className="form-group mb-6 col-12"><Select name="tenderStates" options={options} value={selected} placeholder={loadingStates ? "Loading states..." : requiredSelectPlaceholder(placeholder)} isMulti isClearable isDisabled={loadingStates} isLoading={loadingStates} closeMenuOnSelect={false} menuPortalTarget={typeof document !== "undefined" ? document.body : null} onChange={(items) => { const raw = Array.isArray(items) ? items.map((i) => i.value || i) : []; const hasAllIndia = raw.some((item) => token(item) === ALL_INDIA_STATE_OPTION.value); const next = hasAllIndia ? [ALL_INDIA_STATE_OPTION.value] : (tenderStateLimit ? raw.slice(0, tenderStateLimit) : raw); setField("tenderStates", unique(next)); setTouched((p) => ({ ...p, tenderStates: true })); }} onBlur={() => markTouched("tenderStates", selectedTenderStates.length ? "" : "At least 1 state is required")} />{fieldError("tenderStates")}</div>;
  };
  const renderClassificationSelects = () => <>
    <div className="form-group mb-6 col-12"><Select name="subcategory" options={subcategoryOptions} value={selectedSubcategoryOptions} placeholder="Subcategory" isMulti isDisabled isSearchable menuPortalTarget={typeof document !== "undefined" ? document.body : null} /></div>
    <div className="form-group mb-6 col-12"><Select name="products" options={productSelectOptions} value={selectedProducts.map((p) => productSelectOptions.find((o) => o.value === p) || { value: p, label: p })} placeholder={loadingProducts ? "Loading products..." : "Select products"} isMulti isClearable isSearchable isLoading={loadingProducts} isDisabled={loadingProducts || selectedSubcategories.length === 0} closeMenuOnSelect={false} menuPortalTarget={typeof document !== "undefined" ? document.body : null} onChange={(items) => { const next = Array.isArray(items) ? items.map((i) => i.value || i) : []; setField("products", unique(next)); setField("keywords", []); setTouched((p) => ({ ...p, products: true })); }} />{fieldError("products")}</div>
    <div className="form-group mb-6 col-12"><Select name="keywords" options={keywordSelectOptions} value={selectedKeywords.map((k) => keywordSelectOptions.find((o) => o.value === k) || { value: k, label: k })} placeholder={loadingKeywords ? "Loading keywords..." : "Select keywords"} isMulti isClearable isSearchable isLoading={loadingKeywords} isDisabled={loadingKeywords || selectedSubcategories.length === 0} closeMenuOnSelect={false} menuPortalTarget={typeof document !== "undefined" ? document.body : null} onChange={(items) => { const next = Array.isArray(items) ? items.map((i) => i.value || i) : []; setField("keywords", unique(next)); setTouched((p) => ({ ...p, keywords: true })); }} />{fieldError("keywords")}</div>
  </>;
  if (!isOpen && !isClosing) return null;
  const fadeClass = isClosing ? "fade" : "fade show";
  return (
    <div className={`pos-abs-tl w-100 h-100 ${fadeClass} d-flex align-items-start justify-content-center px-6 pb-6 z-index-supper`} style={{ paddingTop: "140px" }} onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }} role="presentation">
      <div className="pos-abs-tl w-100 h-100 bg-dark-opacity-7" style={{ backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)" }} />
      <div className="modal d-block position-relative z-index-2" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-scrollable max-width-px-840 mx-auto" role="document">
          <div className="modal-content bg-white rounded-8 shadow-8 overflow-hidden activation-form-modal">
            <style>{`
              .activation-form-modal .activation-required-field { position: relative; }
              .activation-form-modal .activation-required-placeholder {
                position: absolute;
                left: 1rem;
                top: 50%;
                max-width: calc(100% - 3.25rem);
                color: #6c757d;
                line-height: 1;
                pointer-events: none;
                transform: translateY(-50%);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                z-index: 2;
              }
              .activation-form-modal .activation-required-placeholder.is-textarea {
                top: 1rem;
                transform: none;
              }
              .activation-form-modal .activation-required-asterisk { color: #dc3545; }
              .activation-form-modal .activation-required-control-empty { color: transparent; }
              .activation-form-modal .activation-required-control-empty option { color: #495057; }
            `}</style>
            <div className="modal-header bg-primary text-white p-7 position-relative border-0">
              <button type="button" className="circle-32 btn-reset bg-white pos-abs-tr mt-5 mr-5 focus-reset z-index-3" onClick={close} aria-label="Close"><i className="fas fa-times text-primary" /></button>
              <div className="w-100"><h3 className="font-size-6 font-weight-bold text-white mb-2">Activation Process</h3><p className="font-size-4 text-white gr-opacity-8 mb-0">{combinedPlanText || "Complete activation details."}</p></div>
            </div>
            {loading ? (
              <div className="modal-body p-7 pre-scrollable" style={{ minHeight: "55vh", maxHeight: "75vh" }}><div className="d-flex align-items-center justify-content-center h-100 py-10"><div className="text-center"><span className="spinner-border text-primary mb-4" role="status" aria-hidden="true" /><p className="font-size-4 text-gray mb-0">Loading activation details...</p></div></div></div>
            ) : (
              <div className="modal-body p-7 pre-scrollable" style={{ minHeight: "55vh", maxHeight: "75vh" }}>
                {submitState === "success" ? <div className="alert alert-success mb-6" role="alert"><i className="fas fa-check-circle mr-2" /> Saved successfully.</div> : null}
                {submitState === "error" ? <div className="alert alert-danger mb-6" role="alert"><i className="fas fa-exclamation-circle mr-2" />{submitApiError || "Something went wrong. Please try again."}</div> : null}
                {touched.planSummary && errors.planSummary ? <div className="alert alert-danger mb-6" role="alert"><i className="fas fa-exclamation-circle mr-2" /> {errors.planSummary}</div> : null}
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    {input({ name: "gst", placeholder: "GST No.", required: true, validate: (raw) => (!text(raw) ? "GST No. is required" : "") })}
                    {input({ name: "companyName", placeholder: "Company name", required: true, validate: (raw) => (!text(raw) ? "Company name is required" : ""), readOnly: Boolean(gstLockedFields.companyName) })}
                    <div className="form-group mb-6 col-12 activation-required-field"><select id="companyType" name="companyType" aria-label="Company type" className={`${controlClass("companyType")} ${!text(value("companyType")) ? "activation-required-control-empty" : ""}`} value={value("companyType")} onChange={(e) => setField("companyType", e.target.value)} onBlur={() => markTouched("companyType", !text(value("companyType")) ? "Company type is required" : "")} disabled={Boolean(gstLockedFields.companyType)} required><option value=""></option>{COMPANY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>{requiredPlaceholder("Company type", !text(value("companyType")))}{fieldError("companyType")}</div>
                    {input({ name: "contactPerson", placeholder: "Contact person name", required: true, validate: (raw) => (!text(raw) ? "Contact person is required" : "") })}
                    {input({ name: "designation", placeholder: "Designation" })}
                    {input({ name: "address", placeholder: "Full address", required: true, rows: 3, validate: (raw) => (!text(raw) ? "Address is required" : "") })}
                    {renderStateSelect()}
                    {renderCitySelect()}
                    {input({ name: "phone", type: "tel", placeholder: "Phone (optional)", validate: phoneError })}
                    {input({ name: "mobile", type: "tel", placeholder: "Mobile", required: true, validate: mobileError })}
                    {input({ name: "email", type: "email", placeholder: "Email", required: true, validate: (raw) => { const email = text(raw); if (!email) return "Email is required"; return EMAIL_RE.test(email) ? "" : "Enter a valid email address"; } })}
                    {input({ name: "altEmail", type: "email", placeholder: "Alternate email", validate: (raw) => { const email = text(raw); return !email || EMAIL_RE.test(email) ? "" : "Enter a valid email address"; } })}
                    {input({ name: "website", placeholder: "Website (optional)", validate: websiteError })}
                    {renderTenderStates()}
                    {renderClassificationSelects()}
                    {input({ name: "productKeywords", placeholder: "Product keywords (comma-separated)", required: true, rows: 3, validate: () => (productKeywords.length ? "" : "Product keywords are required") })}
                    {input({ name: "planText", placeholder: "Subscription plan", required: true, readOnly: true, overrideValue: combinedPlanText })}
                    {input({ name: "amountText", placeholder: "Amount", required: true, readOnly: true, overrideValue: combinedCostText })}
                    {hasGemPlan ? input({ name: "gemUserName", placeholder: "GeM username/email", required: true, validate: (raw) => (!text(raw) ? "GeM username/email is required" : "") }) : null}
                    {hasGemPlan ? input({ name: "gemPassword", type: "password", placeholder: "GeM password", required: true, validate: (raw) => (!text(raw) ? "GeM password is required" : "") }) : null}
                    {input({ name: "remarks", placeholder: "Remarks (optional)", rows: 3 })}
                  </div>
                  <div className="pt-4 border-top border-default-color-2"><div className="form-group mb-0"><div className="d-flex align-items-start"><input type="checkbox" id="terms-checkbox" checked={termsAccepted} onChange={(e) => { setTermsAccepted(e.target.checked); setTermsError(""); }} className="mt-1 mr-2" style={{ cursor: "pointer" }} /><label htmlFor="terms-checkbox" className="mb-0" style={{ cursor: "pointer", fontSize: "0.875rem" }}>I have read and agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary" style={{ textDecoration: "underline" }}>Terms and Conditions</a></label></div>{termsError ? <div className="text-danger mt-2" style={{ fontSize: "0.875rem" }}>{termsError}</div> : null}</div></div>
                  <div className="d-flex justify-content-end align-items-center pt-4"><button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? <><span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />Submitting</> : "Submit"}</button></div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivationFormMain;